<template>
  <v-container fluid>
    <!-- FILTROS -->
    <v-card outlined class="pa-4 mb-6">
      <v-card-title class="text-h6 font-weight-bold">
        Consulta de documentos fiscales
      </v-card-title>

      <v-card-subtitle class="pb-4">
        Consulta y descarga tus facturas aplicando los filtros deseados.
      </v-card-subtitle>

      <v-card-text>
        <v-row dense>
          <!-- RANGO DE FECHAS -->
          <v-col cols="12" md="4">
            <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="280"
                min-width="280"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="formattedDateRange"
                  label="Periodo de facturación"
                  readonly
                  prepend-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>

              <v-date-picker
                v-model="dateRange"
                range
                locale="es-mx"
                :max="today"
                @change="dateMenu = false"
              />
            </v-menu>
          </v-col>

          <!-- PRODUCTO -->
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.products"
              :items="selectableProducts"
              label="Producto"
              multiple
              clearable
              chips
            />
          </v-col>

          <!-- FOLIO -->
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.folios"
              :items="folios"
              label="Folio"
              multiple
              clearable
              chips
            />
          </v-col>

          <!-- TIPO -->
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.types"
              :items="types"
              label="Tipo de comprobante"
              multiple
              clearable
              chips
            />
          </v-col>

          <!-- MONEDA -->
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.currencies"
              :items="currencies"
              label="Moneda"
              multiple
              clearable
              chips
            />
          </v-col>

          <!-- ESTATUS -->
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.statuses"
              :items="statuses"
              label="Estatus"
              multiple
              clearable
              chips
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn text @click="resetFilters">
          Limpiar filtros
        </v-btn>

        <v-btn color="primary" @click="search">
          Consultar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- RESULTADOS -->
    <v-card outlined>
      <v-card-title class="text-h6 font-weight-bold">
        Facturas encontradas
        <v-spacer/>
        <export-table v-if="invoices.length"
            :fileName="exportFileName"
            :mapFunction="mapFunction"
            :dataToExport="invoices"
            :headers="excelHeaders"
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="invoices"
        item-key="invoiceId"
        class="elevation-0"
      >
        <template v-slot:[`item.creditFolios`]="{ item }">
            <div class="d-flex flex-wrap align-center">
                <!-- Mostrar máximo 2 folios -->
                <v-chip
                v-for="folio in item.creditFolios.slice(0, 2)"
                :key="folio"
                small
                class="mr-1"
                color="primary"
                text-color="white"
                >
                {{ folio }}
                </v-chip>

                <!-- Indicador de más folios -->
                <v-tooltip bottom v-if="item.creditFolios.length > 2">
                <template v-slot:activator="{ on, attrs }">
                    <v-chip
                    small
                    outlined
                    class="mr-1"
                    v-bind="attrs"
                    v-on="on"
                    >
                    +{{ item.creditFolios.length - 2 }} más
                    </v-chip>
                </template>

                <div>
                    <div
                    v-for="folio in item.creditFolios"
                    :key="folio"
                    >
                    {{ folio }}
                    </div>
                </div>
                </v-tooltip>
            </div>
        </template>

        <template v-slot:[`item.emittionDate`]="{ item }">
          {{ formatDateShortText(item.emittionDate) }}
        </template>

        <template v-slot:[`item.description`]="{ item }">
            {{ item.description || '—' }}
        </template>

        <template v-slot:[`item.invoiceType`]="{ item }">
          {{ item.invoiceType === 'I' ? 'Factura' : 'Complemento de Pago'}}
        </template>

        <template v-slot:[`item.amount`]="{ item }">
          {{ formatCurrency(item.amount) }}
        </template>

        <template v-slot:[`item.iva`]="{ item }">
          {{ formatCurrency(item.iva) }}
        </template>

        <template v-slot:[`item.folio`]="{ item }">
          {{ item.serie }} {{ item.folio }}
        </template>

        <template v-slot:[`item.active`]="{ item }">
          {{ item.active ? 'Activa' : 'Cancelada' }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!item.facturamaId"
                >
                  <v-icon small color="primary">
                    mdi-download
                  </v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item @click="onInvoiceDownload(item, 'pdf')">
                  <v-list-item-icon>
                    <v-icon small color="primary">mdi-file-pdf-box</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>PDF</v-list-item-title>
                </v-list-item>

                <v-list-item @click="onInvoiceDownload(item, 'xml')">
                  <v-list-item-icon>
                    <v-icon small color="primary">mdi-xml</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>XML</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

        <template v-slot:no-data>
          No se encontraron facturas con los filtros seleccionados.
        </template>
      </v-data-table>
      <!-- Inicio - Para que no el botón de pregunra no tape las flechas de paginación de la tabla -->
      <v-card-actions class="mb-4">
      </v-card-actions>
      <!-- Fin -->
    </v-card>
  </v-container>
</template>

<script>
import moment from 'moment'
import productMixin from '../../mixins/productMixin'
import InvoiceService from '../../services/InvoiceService'
import ProductService from '../../services/ProductService'
import ExportTable from '../../components/ExportTable.vue'

export default {
  name: 'BillingInquiry',
  components: { ExportTable },
  mixins: [productMixin],

  data () {
    return {
      today: moment().format('YYYY-MM-DD'),
      dateMenu: false,
      bluProducts: [],

      dateRange: [
        moment().subtract(3, 'months').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ],

      filters: {
        dateFrom: '',
        dateTo: '',
        products: [],
        folios: [],
        types: [],
        currencies: [],
        statuses: []
      },

      invoices: [],

      // Catálogos
      types: ['Factura', 'Complemento de Pago'],
      currencies: ['MXN', 'USD'],
      statuses: ['Activa', 'Cancelada'],

      headers: [
        { text: 'Folio del crédito', value: 'creditFolios', align: 'left' },
        { text: 'Fecha de emisión', value: 'emittionDate', align: 'center' },
        { text: 'Descripción', value: 'description', align: 'left' },
        { text: 'Tipo', value: 'invoiceType', align: 'left' },
        { text: 'Moneda', value: 'currency', align: 'center' },
        { text: 'Total', value: 'amount', align: 'right' },
        { text: 'I.V.A.', value: 'iva', align: 'right' },
        { text: 'Folio', value: 'folio', align: 'left' },
        { text: 'UUID', value: 'uuid', align: 'left' },
        { text: 'Estatus', value: 'active', align: 'center' },
        { text: 'Descargar', value: 'actions', align: 'center', sortable: false }
      ],
      invoicesFiltered: [],
      excelHeaders: {
        creditFolios: 'Folio del Crédito',
        emittionDate: 'Fecha Emisión',
        description: 'Descripción',
        invoiceType: 'Tipo',
        currency: 'Moneda',
        amount: 'Total',
        iva: 'I.V.A.',
        folio: 'Folio',
        uuid: 'UUID',
        active: 'Estatus'
      }
    }
  },
  watch: {
    dateRange ([a, b]) {
      if (!a || !b) return

      const start = moment.min(moment(a), moment(b)).format('YYYY/MM/DD')
      const end = moment.max(moment(a), moment(b)).format('YYYY/MM/DD')

      this.filters.dateFrom = start
      this.filters.dateTo = end
    },
    accessiblePortfolio () {
      return this.resetFilters()
    }
  },

  created () {
    this.loadProducts()
    this.initializeDateRange()
  },

  methods: {
    async loadProducts () {
      const { data } = await ProductService.getProducts()

      // Por el momento se excluyen CCF y CC
      this.bluProducts = data?.filter(p => !['CCF', 'CC'].includes(p.code))
    },
    initializeDateRange () {
      this.dateRange = [
        moment().subtract(3, 'months').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ]
    },

    async search () {
      const loader = this.$loading.show()
      try {
        const payload = {
          clientId: this.$store?.getters?.user?.clientId,
          startDate: this.filters.dateFrom,
          endDate: this.filters.dateTo
        }

        if (this.filters.types.length === 1 && this.filters.types[0] === 'Complemento de Pago') payload.invoiceType = 'P'
        if (this.filters.types.length === 1 && this.filters.types[0] === 'Factura') payload.invoiceType = 'I'

        if (this.filters.statuses.length === 1 && this.filters.statuses[0] === 'Activa') payload.active = true
        if (this.filters.statuses.length === 1 && this.filters.statuses[0] === 'Cancelada') payload.active = false

        if (this.filters.products.length) {
          payload.products = this.bluProducts.filter(p => this.filters.products.includes(p.name)).map(p => p.productId)
        }

        const { invoices } = await InvoiceService.listInvoices(payload)
        let filteredInvoices = invoices

        // Filtrar por moneda
        if (this.filters.currencies.length === 1) {
          filteredInvoices = filteredInvoices.filter(i => i.currency === this.filters.currencies[0])
        }

        // Filtrar por folios
        if (this.filters.folios.length) {
          filteredInvoices = filteredInvoices.filter(i => i.creditFolios?.some(folio =>
            this.filters.folios.includes(folio?.split('-')[0])
          ))
        }

        // Descripción
        const factoring = this.bluProducts.filter(p => ['FB', 'BI'].includes(p.code))

        filteredInvoices.forEach(i => {
          const isFactoring = factoring.map(f => f.productId).includes(i.productId)
          const isPaymentComplement = i.invoiceType === 'P'

          if (!isPaymentComplement) {
            if (isFactoring) {
              const newDesc = i.description?.split('de ')[0] + 'del ' + i.description?.split('del ')[1]
              i.description = newDesc
            }
          } else {
            const newDescCP = i.description?.split('de ')[0] + 'correspondiente al pago del ' + i.description?.split('al ')[1]
            i.description = newDescCP
          }
        })

        this.invoices = filteredInvoices.filter(i => i.uuid)
      } catch (err) {
        console.error(err)
        this.$swal({
          icon: 'error',
          title: 'Ocurrió un error',
          text: 'Hubo un problema al cargar los resultados.',
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#d93025'
        })
      } finally {
        loader.hide()
      }
    },

    onInvoiceDownload (item, format) {
      this.downloadInvoice({
        id: item.facturamaId,
        fileType: format,
        fileName: item.description
      })
    },

    mapFunction (item) {
      return Object.assign({
        creditFolios: item.creditFolios?.join(','),
        emittionDate: this.formatDateShortText(item.emittionDate),
        description: item.description,
        invoiceType: item.invoiceType === 'I' ? 'Factura' : 'Complemento de Pago',
        currency: item.currency,
        amount: this.formatCurrency(item.amount),
        iva: this.formatCurrency(item.iva),
        folio: item.folio,
        uuid: item.uuid,
        active: item.active ? 'Activa' : 'Cancelada'
      })
    },

    resetFilters () {
      this.filters = {
        dateFrom: '',
        dateTo: '',
        products: this.selectableProducts,
        folios: [],
        types: [],
        currencies: [],
        statuses: ['Activa']
      }

      this.initializeDateRange()
    }
  },
  computed: {
    formattedDateRange () {
      return `${moment(this.filters.dateFrom || this.dateRange[0]).format('DD/MM/YYYY')} – ${moment(this.filters.dateTo || this.dateRange[1]).format('DD/MM/YYYY')}`
    },
    selectableProducts () {
      return [...new Set(
        this.accessiblePortfolio
          ?.map(c => c.product?.name)
          .filter(Boolean)
      )]
    },
    folios () {
      return this.accessiblePortfolio
        .filter(c => this.filters.products.length ? this.filters.products.includes(c.product?.name) : this.selectableProducts.includes(c.product?.name))
        .map(c => c.folio)
    },
    exportFileName () {
      const f = this.filters
      let from = ''
      let to = ''

      // Rango de fechas
      if (f.dateFrom && f.dateTo) {
        from = moment(f.dateFrom).format('DDMMYY')
        to = moment(f.dateTo).format('DDMMYY')
      }

      return `Facturas Blu ${from} - ${to}`
    }
  }
}
</script>
